import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, comics } from '@/mocks/comics';
import type { ComicItem } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'hanman');
  const [filteredComics, setFilteredComics] = useState<ComicItem[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ongoing' | 'completed'>('all');

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    let result = getComicsByCategory(activeCategory);

    if (statusFilter === 'ongoing') {
      result = result.filter((c) => c.status === '连载中');
    } else if (statusFilter === 'completed') {
      result = result.filter((c) => c.status === '已完结');
    }

    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }

    setFilteredComics(result);
  }, [activeCategory, sortBy, statusFilter]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
            <span>/</span>
            <span className="text-foreground-900 font-medium">{currentCategory?.name || '分类'}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {currentCategory?.name || '漫画'}专区
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部{currentCategory?.name}漫画作品
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200/70">
            <div className="flex flex-wrap gap-1.5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setSortBy('latest')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'latest' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy('popular')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'popular' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最热
              </button>
              <button
                onClick={() => setSortBy('rating')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'rating' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                评分
              </button>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  statusFilter === 'all' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setStatusFilter('ongoing')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  statusFilter === 'ongoing' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                连载中
              </button>
              <button
                onClick={() => setStatusFilter('completed')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  statusFilter === 'completed' ? 'bg-accent-100 text-accent-700' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                已完结
              </button>
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6 pb-14" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="large" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
              <Link to="/" className="inline-block mt-3 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200">
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}